import { GoogleGenerativeAI } from '@google/generative-ai';

export class ChatManager {
    constructor(settings) {
        this.settings = settings;
        this.chatHistory = [];
        this.chatSession = null;
    }

    async initializeChat() {
        const { apiKey, temperature, systemPrompt } = this.settings.getSettings();
        
        if (!apiKey) {
            throw new Error('Please enter your API key in settings (gear icon)');
        }

        const genAI = new GoogleGenerativeAI(apiKey);
        const model = genAI.getGenerativeModel({
            model: "gemini-1.5-flash",
            systemInstruction: systemPrompt,
        });

        const generationConfig = {
            temperature,
            topP: 0.95,
            topK: 40,
            maxOutputTokens: 8192,
            responseMimeType: "text/plain",
        };

        this.chatSession = model.startChat({
            generationConfig,
            history: this.chatHistory,
        });
    }

    async sendMessage(message, retryCount = 0) {
        if (!this.chatSession) {
            await this.initializeChat();
        }

        try {
            const result = await this.chatSession.sendMessage(message);
            const response = result.response.text();

            // Update chat history
            this.chatHistory.push(
                { role: "user", parts: [{ text: message }] },
                { role: "model", parts: [{ text: response }] }
            );

            return response;
        } catch (error) {
            console.error('Error sending message:', error);
            
            // Check if it's a rate limit error (429)
            const is429Error = error.message?.includes('429') || 
                             error.message?.includes('Resource has been exhausted') ||
                             error.status === 429;

            if (is429Error && retryCount < 3) {
                console.log(`Rate limit hit, retrying in 10 seconds... (Attempt ${retryCount + 1}/3)`);
                
                // Wait 10 seconds before retrying
                await new Promise(resolve => setTimeout(resolve, 10000));
                
                // Retry the request
                return this.sendMessage(message, retryCount + 1);
            }
            
            // If we've exhausted retries or it's a different error, throw with a user-friendly message
            if (is429Error) {
                throw new Error('The AI service is currently busy. Please try again in a few minutes.');
            } else {
                throw new Error(error.message || 'An error occurred while sending the message.');
            }
        }
    }

    clearChat() {
        this.chatHistory = [];
        this.chatSession = null;
    }
}
